<script setup lang="ts">
import { computePosition, flip, shift } from '@floating-ui/dom';
import Mention from '@tiptap/extension-mention';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import StarterKit from '@tiptap/starter-kit';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import { VueRenderer, posToDOMRect } from '@tiptap/vue-3';
import { useUiStore } from '@/store/uiStore';
import AppUserMentionList from './AppUserMentionList.vue';
import AppWysiwygEditorToolbar from './AppWysiwygEditorToolbar.vue';
import { Iframe } from './IframeExtension';
import { ResizableImage } from './ResizableImageExtension';
import type { UserSummary } from '@beeboard/common';
import type { Editor } from '@tiptap/core';
import type {
  SuggestionProps,
  SuggestionKeyDownProps,
} from '@tiptap/suggestion';

export type TipCommand =
  | 'toggleBold'
  | 'toggleItalic'
  | 'setParagraph'
  | 'toggleLevelOneHeading'
  | 'toggleLevelTwoHeading'
  | 'toggleLevelThreeHeading'
  | 'toggleBulletList'
  | 'toggleOrderedList'
  | 'undo'
  | 'insertTable'
  | 'addRowBefore'
  | 'addRowAfter'
  | 'deleteRow'
  | 'addColumnBefore'
  | 'addColumnAfter'
  | 'deleteColumn'
  | 'deleteTable'
  | 'setLink'
  | 'insertImage'
  | 'insertEmbed';

interface Props {
  modelValue?: string | null;
  toolBarPosition?: 'bottom' | 'top';
  label?: string;
  errors?: string[];
  usersToMention?: UserSummary[];
  enableMentions?: boolean;
  enableDictation?: boolean;
  enableTables?: boolean;
  enableLinks?: boolean;
  enableImages?: boolean;
  enableEmbeds?: boolean;
  contentWidth?: string;
}

const COMPONENT_ESC_KEY = 'AppWysiwygEditor';

const props = withDefaults(defineProps<Props>(), {
  toolBarPosition: 'top',
  label: '',
  errors: () => [],
  modelValue: null,
  usersToMention: () => [],
  enableMentions: false,
  enableDictation: false,
  enableTables: false,
  enableLinks: false,
  enableImages: false,
  enableEmbeds: false,
  contentWidth: undefined,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const getUserDisplayName = (user: UserSummary) => {
  const firstName = user.first_name || '';
  const lastName = user.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || user.username || 'Unknown User';
};

const updatePosition = (editor: Editor, element: HTMLElement) => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to,
      ),
  };

  computePosition(virtualElement, element, {
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [shift(), flip()],
  })
    .then(({ x, y, strategy }) => {
      element.style.width = 'max-content';
      element.style.position = strategy;
      element.style.left = `${x}px`;
      element.style.top = `${y}px`;
    })
    .catch((error) => {
      console.warn('Failed to position mention dropdown:', error);
    });
};

const uiStore = useUiStore();

const MentionExtension = Mention.configure({
  HTMLAttributes: {
    class: 'mention',
  },
  suggestion: {
    items: ({ query }: { query: string }) => {
      const filtered = props.usersToMention
        .filter((user) => {
          const displayName = getUserDisplayName(user);
          const matches = displayName
            .toLowerCase()
            .includes(query.toLowerCase());

          return matches;
        })
        .slice(0, 10);

      return filtered;
    },
    render: () => {
      let component: VueRenderer;

      return {
        onStart: (suggestionProps: SuggestionProps) => {
          // Guard: Check if editor is in valid state
          try {
            if (!suggestionProps?.editor?.view?.dom) {
              return;
            }
          } catch {
            // 'Editor not available, aborting mention dropdown:',
            return;
          }

          // Add high-priority keymap that blocks Escape from reaching other handlers
          // and allows the mention dropdown to close
          uiStore.addKeymap({ Escape: () => {} }, COMPONENT_ESC_KEY, false);

          component = new VueRenderer(AppUserMentionList, {
            props: suggestionProps,
            editor: suggestionProps.editor,
          });

          if (!suggestionProps.clientRect || !component.element) {
            return;
          }

          const element = component.element as HTMLElement;
          element.style.position = 'absolute';
          document.body.appendChild(element);
          updatePosition(suggestionProps.editor, element);
        },

        onUpdate: (suggestionProps: SuggestionProps) => {
          component.updateProps(suggestionProps);

          if (!suggestionProps.clientRect || !component.element) {
            return;
          }

          const element = component.element as HTMLElement;
          updatePosition(suggestionProps.editor, element);
        },

        onKeyDown: (suggestionProps: SuggestionKeyDownProps) => {
          if (suggestionProps.event.key === 'Escape') {
            component.destroy();

            return true;
          }

          return component.ref?.onKeyDown({ event: suggestionProps.event });
        },

        onExit: () => {
          component.destroy();
          setTimeout(() => {
            // Small delay to allow event processing to complete
            uiStore.removeKeymap(COMPONENT_ESC_KEY);
          });
        },
      };
    },
  },
});

const ModifiedStarterKit = StarterKit.configure({
  paragraph: {
    HTMLAttributes: {
      style: 'min-height: 1rem',
    },
  },
  link: props.enableLinks
    ? { openOnClick: false, HTMLAttributes: { class: 'editor-link' } }
    : false,
});

const ImageExtension = ResizableImage.configure({
  inline: false,
  allowBase64: false,
  HTMLAttributes: { class: 'editor-image' },
});

const IframeExtension = Iframe;

const TableExtension = Table.configure({
  resizable: false,
  HTMLAttributes: {
    class: 'tiptap-table',
  },
});

const TableRowExtension = TableRow.configure({});
const TableCellExtension = TableCell.configure({});
const TableHeaderExtension = TableHeader.configure({});

const tableExtensions = [
  TableExtension,
  TableRowExtension,
  TableCellExtension,
  TableHeaderExtension,
];

// Reactive counter to force toolbar state recalculation on selection changes
const editorStateVersion = ref(0);

// Build extensions array based on enabled features
const buildExtensions = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const extensions: any[] = [ModifiedStarterKit];
  if (props.enableMentions) {
    extensions.push(MentionExtension);
  }
  if (props.enableTables) {
    extensions.push(...tableExtensions);
  }
  if (props.enableImages) {
    extensions.push(ImageExtension);
  }
  if (props.enableEmbeds) {
    extensions.push(IframeExtension);
  }

  return extensions;
};

const editor = useEditor({
  content: props.modelValue,
  extensions: buildExtensions(),
  editable: true,
  injectCSS: false,
  onUpdate({ editor, transaction }) {
    if (!transaction.docChanged) {
      return;
    }
    let updatedContent = editor.getHTML();
    if (updatedContent === '<p style="min-height: 1rem;"></p>') {
      updatedContent = '';
    }

    emit('update:modelValue', updatedContent);
  },
  onSelectionUpdate() {
    // Trigger toolbar state recalculation when selection changes
    editorStateVersion.value++;
  },
});

const toolbarBindings = computed(() => {
  // Access editorStateVersion to create reactive dependency on selection changes
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  editorStateVersion.value;

  return {
    isBoldActive: editor.value?.isActive('bold'),
    isItalicActive: editor.value?.isActive('italic'),
    isParagraphActive: editor.value?.isActive('paragraph'),
    isLevelOneHeadingActive: editor.value?.isActive('heading', { level: 1 }),
    isLevelTwoHeadingActive: editor.value?.isActive('heading', { level: 2 }),
    isLevelThreeHeadingActive: editor.value?.isActive('heading', { level: 3 }),
    isBulletListActive: editor.value?.isActive('bulletList'),
    isOrderedListActive: editor.value?.isActive('orderedList'),
    isTableActive: editor.value?.isActive('table'),
    isLinkActive: editor.value?.isActive('link'),
    enableTables: props.enableTables,
    enableLinks: props.enableLinks,
    enableImages: props.enableImages,
    enableEmbeds: props.enableEmbeds,
  };
});

const toEmbedUrl = (url: string): string => {
  // YouTube
  const ytMatch = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/,
  );
  if (ytMatch) {
    return `https://www.youtube.com/embed/${ytMatch[1]}`;
  }
  // Microsoft Stream (classic)
  const streamMatch = url.match(/microsoftstream\.com\/video\/([a-f0-9-]+)/i);
  if (streamMatch) {
    return `https://web.microsoftstream.com/embed/video/${streamMatch[1]}`;
  }
  // SharePoint / OneDrive embed
  if (url.includes('sharepoint.com') || url.includes('onedrive.live.com')) {
    const embedUrl = new URL(url);
    embedUrl.searchParams.set('action', 'embedview');

    return embedUrl.toString();
  }
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  // Already an embed/iframe URL — use as-is
  return url;
};

const toggle = (param: TipCommand) => {
  switch (param) {
    case 'toggleBold':
      editor.value?.chain().focus().toggleBold().run();
      break;
    case 'toggleItalic':
      editor.value?.chain().focus().toggleItalic().run();
      break;
    case 'setParagraph':
      editor.value?.chain().focus().setParagraph().run();
      break;
    case 'toggleLevelOneHeading':
      editor.value?.chain().focus().toggleHeading({ level: 1 }).run();
      break;
    case 'toggleLevelTwoHeading':
      editor.value?.chain().focus().toggleHeading({ level: 2 }).run();
      break;
    case 'toggleLevelThreeHeading':
      editor.value?.chain().focus().toggleHeading({ level: 3 }).run();
      break;
    case 'toggleBulletList':
      editor.value?.chain().focus().toggleBulletList().run();
      break;
    case 'toggleOrderedList':
      editor.value?.chain().focus().toggleOrderedList().run();
      break;
    case 'undo':
      editor.value?.chain().undo().focus().run();
      break;
    case 'insertTable':
      editor.value
        ?.chain()
        .focus()
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
      break;
    case 'addRowBefore':
      editor.value?.chain().focus().addRowBefore().run();
      break;
    case 'addRowAfter':
      editor.value?.chain().focus().addRowAfter().run();
      break;
    case 'deleteRow':
      editor.value?.chain().focus().deleteRow().run();
      break;
    case 'addColumnBefore':
      editor.value?.chain().focus().addColumnBefore().run();
      break;
    case 'addColumnAfter':
      editor.value?.chain().focus().addColumnAfter().run();
      break;
    case 'deleteColumn':
      editor.value?.chain().focus().deleteColumn().run();
      break;
    case 'deleteTable':
      editor.value?.chain().focus().deleteTable().run();
      break;
    case 'setLink':
      openLinkDialog();
      break;
    case 'insertImage':
      openImageDialog();
      break;
    case 'insertEmbed':
      openEmbedDialog();
      break;
  }
};

// --- Media dialog state ---
const activeDialog = ref<'link' | 'image' | 'embed' | null>(null);
const showDialog = computed({
  get: () => activeDialog.value !== null,
  set: (val: boolean) => {
    if (!val) {
      activeDialog.value = null;
    }
  },
});
const dialogUrl = ref('');

function openLinkDialog() {
  const previousUrl = editor.value?.getAttributes('link').href ?? '';
  dialogUrl.value = previousUrl;
  activeDialog.value = 'link';
}

function openImageDialog() {
  dialogUrl.value = '';
  activeDialog.value = 'image';
}

function openEmbedDialog() {
  dialogUrl.value = '';
  activeDialog.value = 'embed';
}

function closeDialog() {
  activeDialog.value = null;
  editor.value?.chain().focus().run();
}

function confirmLink() {
  const url = dialogUrl.value.trim();
  if (url === '') {
    editor.value?.chain().focus().extendMarkRange('link').unsetLink().run();
  } else {
    editor.value
      ?.chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url })
      .run();
  }
  activeDialog.value = null;
}

function confirmImage() {
  const src = dialogUrl.value.trim();
  if (!src) {
    return;
  }
  editor.value?.chain().focus().setImage({ src }).run();
  activeDialog.value = null;
}

function confirmEmbed() {
  const url = dialogUrl.value.trim();
  if (!url) {
    return;
  }
  const embedSrc = toEmbedUrl(url);
  editor.value?.chain().focus().setIframe({ src: embedSrc }).run();
  activeDialog.value = null;
}

const setFocus = () => editor.value?.view.focus();

const toolbarRef = ref<InstanceType<typeof AppWysiwygEditorToolbar>>();
const dictationError = ref<string>('');
const showDictationError = ref(false);

const handleDictationError = (message: string) => {
  dictationError.value = message;
  showDictationError.value = true;
  setTimeout(() => {
    showDictationError.value = false;
  }, 5000);
};

const handleKeyDown = (event: KeyboardEvent) => {
  if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
    event.preventDefault();
    toolbarRef.value?.dictationButtonRef?.toggleDictation();
  } else if (
    event.key === 'Escape' &&
    toolbarRef.value?.dictationButtonRef?.isRecording
  ) {
    event.preventDefault();
    toolbarRef.value?.dictationButtonRef?.stopDictation();
  }
};

onMounted(() => {
  if (props.enableDictation) {
    document.addEventListener('keydown', handleKeyDown);
  }
});

onUnmounted(() => {
  if (props.enableDictation) {
    document.removeEventListener('keydown', handleKeyDown);
  }
});

const getMentionedUserIds = (): string[] => {
  if (!editor.value) {
    return [];
  }

  const userIds: string[] = [];

  editor.value.state.doc.descendants((node) => {
    if (node.type.name === 'mention' && node.attrs.id) {
      userIds.push(node.attrs.id);
    }

    return true;
  });

  return [...new Set(userIds)];
};

const stopDictation = () => {
  toolbarRef.value?.dictationButtonRef?.stopDictation();
};

const clear = () => {
  editor.value?.commands.clearContent(false);
};

defineExpose({
  focus: setFocus,
  getMentionedUserIds,
  stopDictation,
  clear,
});

watch(
  () => props.modelValue,
  () => {
    if (!editor.value?.isFocused) {
      editor.value?.commands.setContent(props.modelValue, {
        emitUpdate: false,
      });
    }
  },
);
</script>

<template>
  <div v-if="editor">
    <AppWysiwygEditorToolbar
      v-if="props.toolBarPosition === 'top'"
      ref="toolbarRef"
      v-bind="toolbarBindings"
      :editor="editor"
      :enable-dictation="props.enableDictation"
      @toggle="toggle"
      @dictation-error="handleDictationError"
    />
    <UiField :label="label" :errors="errors">
      <div v-if="showDictationError" class="dictation-error">
        {{ dictationError }}
      </div>
      <div
        class="editor-container"
        :style="
          props.contentWidth
            ? { width: props.contentWidth, maxWidth: '100%' }
            : undefined
        "
        @click="setFocus"
      >
        <editor-content :editor="editor" />
      </div>
    </UiField>
    <AppWysiwygEditorToolbar
      v-if="props.toolBarPosition === 'bottom'"
      ref="toolbarRef"
      class="bottom-toolbar"
      v-bind="toolbarBindings"
      :editor="editor"
      :enable-dictation="props.enableDictation"
      @toggle="toggle"
      @dictation-error="handleDictationError"
    />

    <UiOverlay v-model="showDialog">
      <!-- Link Dialog -->
      <UiModal
        v-if="activeDialog === 'link'"
        :title="$t('wysiwygEditor.link')"
        size="small"
        @close="closeDialog"
      >
        <form class="editor-dialog-form" @submit.prevent="confirmLink">
          <div class="editor-dialog-field">
            <UiInput
              v-model="dialogUrl"
              name="url"
              label="URL"
              type="text"
              placeholder="https://"
            />
          </div>
          <div class="editor-dialog-actions">
            <UiButton variant="default" @click="closeDialog">
              {{ $t('button.cancel') }}
            </UiButton>
            <UiButton type="submit" variant="primary">
              {{ $t('button.save') }}
            </UiButton>
          </div>
        </form>
      </UiModal>

      <!-- Image Dialog -->
      <UiModal
        v-if="activeDialog === 'image'"
        :title="$t('wysiwygEditor.image')"
        size="small"
        @close="closeDialog"
      >
        <form class="editor-dialog-form" @submit.prevent="confirmImage">
          <div class="editor-dialog-field">
            <UiInput
              v-model="dialogUrl"
              name="imageUrl"
              label="URL"
              type="text"
              placeholder="https://"
            />
          </div>
          <div class="editor-dialog-actions">
            <UiButton variant="default" @click="closeDialog">
              {{ $t('button.cancel') }}
            </UiButton>
            <UiButton type="submit" variant="primary">
              {{ $t('wysiwygEditor.insert') }}
            </UiButton>
          </div>
        </form>
      </UiModal>

      <!-- Embed Dialog -->
      <UiModal
        v-if="activeDialog === 'embed'"
        :title="$t('wysiwygEditor.embed')"
        size="small"
        @close="closeDialog"
      >
        <form class="editor-dialog-form" @submit.prevent="confirmEmbed">
          <div class="editor-dialog-field">
            <UiInput
              v-model="dialogUrl"
              name="embedUrl"
              label="URL"
              type="text"
              placeholder="YouTube, Vimeo, Stream, SharePoint..."
            />
          </div>
          <div class="editor-dialog-actions">
            <UiButton variant="default" @click="closeDialog">
              {{ $t('button.cancel') }}
            </UiButton>
            <UiButton type="submit" variant="primary">
              {{ $t('wysiwygEditor.insert') }}
            </UiButton>
          </div>
        </form>
      </UiModal>
    </UiOverlay>
  </div>
</template>

<style lang="scss" scoped>
.editor-container {
  @apply prose;
  padding: var(--space-8);
  border: 1px solid var(--color-gray-300);
  margin-bottom: var(--space-12);
}

.editor-container {
  max-width: 100%;
  min-height: 7em;
  max-height: 17em;
  overflow-y: auto;
  border-radius: var(--border-radius-6);
  background-color: #fff;
  // ProseMirror draws no outline (the unscoped block below); the ring goes
  // round the box, as on UiInput.
  @include focus-ring-within;
}
.bottom-toolbar {
  display: flex;
  justify-content: flex-start;
}

.dictation-error {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: var(--space-8) var(--space-12);
  border-radius: var(--border-radius-6);
  margin-bottom: var(--space-8);
  font-size: var(--font-size-14);
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.editor-container :where(p) {
  margin-top: 0.1em;
  margin-bottom: 0.1em;
}

.editor-dialog-form {
  display: flex;
  flex-direction: column;
  margin-top: var(--space-12);
}

.editor-dialog-field {
  margin-bottom: var(--space-12);
}

.editor-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-12);
  margin-top: var(--space-12);
}
</style>

<style type="scss">
/* Under the container, so it outranks App.vue's global :focus-visible ring
   whatever the load order: the ring goes round .editor-container instead. */
.editor-container .ProseMirror {
  outline: none;
}

.ProseMirror h1 {
  font-size: 1.3rem;
  font-weight: var(--font-weight-bold);
}
.ProseMirror h2 {
  font-size: 1.2rem;
  font-weight: var(--font-weight-bold);
}
.ProseMirror h3 {
  font-weight: var(--font-weight-bold);
}

.bottom-toolbar .icon-button {
  margin-top: -12px;
  border: none;
}

.mention {
  background-color: var(--color-primary-100, #dbeafe);
  color: var(--color-primary-800, #1e40af);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--border-radius-4);
  font-weight: var(--font-weight-medium);
  text-decoration: none;

  &:hover {
    background-color: var(--color-primary-200, #bfdbfe);
  }
}

/* Table styles for TipTap editor */
.ProseMirror table {
  border-collapse: collapse;
  width: 100%;
  margin: var(--space-8) 0;
  table-layout: fixed;
  overflow: hidden;
}

.ProseMirror table td,
.ProseMirror table th {
  border: 1px solid var(--color-gray-300, #d1d5db);
  padding: var(--space-8);
  vertical-align: top;
  min-width: 1em;
  position: relative;
}

.ProseMirror table th {
  background-color: var(--color-gray-100, #f3f4f6);
  font-weight: var(--font-weight-bold);
  text-align: left;
}

.ProseMirror table tr:nth-child(even) td {
  background-color: var(--color-gray-50, #f9fafb);
}

.ProseMirror table p {
  margin: 0;
}

/* Link styles */
.ProseMirror a.editor-link {
  color: var(--color-primary, #2563eb);
  text-decoration: underline;
  cursor: pointer;
}

/* Resizable media node views need margin in the editor */
.ProseMirror .resizable-media {
  margin: var(--space-8) 0;
}
</style>
